"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { JURISDICTIONS } from "@/lib/constants";
import Header from "@/components/Header";
import FeedView from "@/components/FeedView";
import ArchiveView from "@/components/ArchiveView";
import TrendsView from "@/components/TrendsView";

export default function Home() {
  const [activeJur, setActiveJur] = useState("ga");
  const [activeView, setActiveView] = useState("feed");
  const [storedArticles, setStoredArticles] = useState(
    Object.fromEntries(JURISDICTIONS.map((j) => [j.id, []]))
  );
  const [freshArticles, setFreshArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchingJur, setSearchingJur] = useState(null);
  const [error, setError] = useState(null);
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [lastFetch, setLastFetch] = useState({});
  const [archiveFilter, setArchiveFilter] = useState("relevant");
  const cancelRef = useRef(false);

  // Load stored articles from API on mount
  useEffect(() => {
    async function loadAll() {
      const all = {};
      const fetchMap = {};
      for (const j of JURISDICTIONS) {
        try {
          const res = await fetch(`/api/articles?jurisdiction=${j.id}&filter=all`);
          const data = await res.json();
          all[j.id] = data.articles || [];
        } catch {
          all[j.id] = [];
        }
      }
      // Get last fetch times from stats
      try {
        const statsRes = await fetch("/api/stats");
        const stats = await statsRes.json();
        if (stats.lastFetch) {
          for (const entry of stats.lastFetch) {
            fetchMap[entry.jurisdiction] = entry.last_fetched;
          }
        }
      } catch {
        // ignore
      }
      setStoredArticles(all);
      setLastFetch(fetchMap);
      setStorageLoaded(true);
    }
    loadAll();
  }, []);

  // Reload stored articles for a jurisdiction from the DB
  const refreshStored = useCallback(async (jurId) => {
    try {
      const res = await fetch(`/api/articles?jurisdiction=${jurId}&filter=all`);
      const data = await res.json();
      setStoredArticles((prev) => ({
        ...prev,
        [jurId]: data.articles || [],
      }));
    } catch {
      // ignore — local state is still valid
    }
  }, []);

  const handleFetch = useCallback(
    async (mode = "daily") => {
      cancelRef.current = false;
      setLoading(true);
      setError(null);
      setFreshArticles([]);
      setClassifying(false);
      setSaving(false);
      setSearchingJur(activeJur);

      try {
        // Step 1: Start the job (returns immediately)
        const startRes = await fetch("/api/fetch-news", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jurisdiction: activeJur, mode }),
        });
        const startData = await startRes.json();

        if (!startRes.ok) {
          setError(startData.error || "Failed to start search");
          setLoading(false);
          return;
        }

        const { jobId } = startData;

        // Step 2: Poll for completion every 3 seconds
        const poll = async () => {
          const maxPolls = 200; // ~10 minutes
          for (let i = 0; i < maxPolls; i++) {
            await new Promise((r) => setTimeout(r, 3000));

            if (cancelRef.current) return null;

            try {
              const pollRes = await fetch(`/api/fetch-news?jobId=${jobId}`);
              const pollData = await pollRes.json();

              if (pollData.status === "classifying") {
                setLoading(false);
                setClassifying(true);
              } else if (pollData.status === "saving") {
                setClassifying(false);
                setSaving(true);
              }

              if (pollData.status === "done") {
                return pollData;
              }
              if (pollData.status === "error") {
                throw new Error(pollData.error || "Search failed");
              }
            } catch (err) {
              if (err.message === "Search failed" || err.message?.includes("failed")) {
                throw err;
              }
              // Network blip — keep polling
            }
          }
          throw new Error("Search timed out after 10 minutes");
        };

        const data = await poll();

        if (!data) return; // cancelled

        setLoading(false);
        setClassifying(false);
        setSaving(false);

        if (data.articles?.length > 0) {
          setFreshArticles(data.articles);
        } else if (data.message) {
          setError(data.message);
        }

        // Refresh stored articles from DB
        await refreshStored(activeJur);
        setLastFetch((prev) => ({
          ...prev,
          [activeJur]: new Date().toISOString(),
        }));
        setSearchingJur(null);
      } catch (err) {
        setLoading(false);
        setClassifying(false);
        setSaving(false);
        setSearchingJur(null);
        setError(err.message);

        // Refresh from DB in case articles were partially saved
        await refreshStored(activeJur);
      }
    },
    [activeJur, refreshStored]
  );

  const handleStopSearch = useCallback(() => {
    cancelRef.current = true;
    setLoading(false);
    setClassifying(false);
    setSaving(false);
    setSearchingJur(null);
    refreshStored(activeJur);
  }, [activeJur, refreshStored]);

  const handleToggleRelevance = useCallback(async (articleId, newRelevant) => {
    // Optimistic update
    setStoredArticles((prev) => {
      const updated = { ...prev };
      for (const jurId of Object.keys(updated)) {
        updated[jurId] = updated[jurId].map((art) =>
          art.id === articleId
            ? {
                ...art,
                manualOverride: true,
                classification: { ...art.classification, relevant: newRelevant },
              }
            : art
        );
      }
      return updated;
    });
    setFreshArticles((prev) =>
      prev.map((art) =>
        art.id === articleId
          ? {
              ...art,
              manualOverride: true,
              classification: { ...art.classification, relevant: newRelevant },
            }
          : art
      )
    );

    try {
      await fetch("/api/articles", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: articleId, relevant: newRelevant }),
      });
    } catch (err) {
      setError(`Override failed: ${err.message}`);
      // Revert on failure
      await refreshStored(activeJur);
    }
  }, [activeJur, refreshStored]);

  const handleClearStorage = useCallback(async () => {
    const jurName = JURISDICTIONS.find((j) => j.id === activeJur)?.name;
    if (
      !confirm(
        `Clear ALL stored articles for ${jurName}? This cannot be undone.`
      )
    )
      return;

    try {
      await fetch(`/api/articles?jurisdiction=${activeJur}`, {
        method: "DELETE",
      });
      setStoredArticles((prev) => ({ ...prev, [activeJur]: [] }));
      setFreshArticles([]);
    } catch (err) {
      setError(`Clear failed: ${err.message}`);
    }
  }, [activeJur]);

  const handleJurChange = (jurId) => {
    setActiveJur(jurId);
    setFreshArticles([]);
    setError(null);
  };

  const currentStored = storedArticles[activeJur] || [];
  const totalArticles = Object.values(storedArticles).flat().length;
  const searchBusy = !!searchingJur;

  const lastFetchDate = lastFetch[activeJur]
    ? new Date(lastFetch[activeJur]).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "never";

  return (
    <div style={{ minHeight: "100vh" }}>
      <Header
        activeView={activeView}
        activeJur={activeJur}
        setActiveJur={handleJurChange}
        totalArticles={totalArticles}
        lastFetchDate={lastFetchDate}
        storedArticles={storedArticles}
        storageLoaded={storageLoaded}
        onFetch={handleFetch}
        searchBusy={searchBusy}
        loading={loading && searchingJur === activeJur}
        classifying={classifying && searchingJur === activeJur}
        saving={saving && searchingJur === activeJur}
      />

      <main style={{ maxWidth: "900px", margin: "0 auto", padding: "20px 28px 100px" }}>
        {error && (
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "4px",
              marginBottom: "14px",
              fontSize: "13px",
              color: "#b91c1c",
              fontFamily: "var(--font-mono)",
            }}
          >
            {error}
          </div>
        )}

        {activeView === "feed" && (
          <FeedView
            activeJur={activeJur}
            loading={loading && searchingJur === activeJur}
            classifying={classifying && searchingJur === activeJur}
            saving={saving && searchingJur === activeJur}
            searchingJur={searchingJur}
            freshArticles={searchingJur === activeJur || !searchingJur ? freshArticles : []}
            currentStored={currentStored}
            error={error}
            onFetch={handleFetch}
            onToggleRelevance={handleToggleRelevance}
            onStopSearch={handleStopSearch}
          />
        )}

        {activeView === "archive" && (
          <ArchiveView
            activeJur={activeJur}
            currentStored={currentStored}
            archiveFilter={archiveFilter}
            setArchiveFilter={setArchiveFilter}
            onClear={handleClearStorage}
            onToggleRelevance={handleToggleRelevance}
          />
        )}

        {activeView === "trends" && <TrendsView allArticles={storedArticles} activeJur={activeJur} />}
      </main>

      {/* Fixed bottom navigation bar */}
      <nav
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#1b1b1a",
          display: "flex",
          justifyContent: "center",
          gap: "0",
          zIndex: 100,
          borderTop: "1px solid #333",
        }}
      >
        {["feed", "archive", "trends"].map((v) => (
          <button
            key={v}
            onClick={() => setActiveView(v)}
            style={{
              padding: "14px 28px",
              border: "none",
              backgroundColor: "transparent",
              color: activeView === v ? "#ff5d31" : "#fff",
              fontSize: "13px",
              fontFamily: "var(--font-mono)",
              fontWeight: activeView === v ? 600 : 400,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              cursor: "pointer",
              borderTop: activeView === v ? "2px solid #ff5d31" : "2px solid transparent",
            }}
          >
            {v}
          </button>
        ))}
      </nav>
    </div>
  );
}
